import { DiscountType, QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { ChargeAttachType } from 'modules/equipment/types';
import { MoneyService, moneyService } from 'shared';
import { BasePrice } from '../../equipment';
import {
  QuoteChargeCalcResult,
  QuoteChargeCalcRow,
  QuoteChargeColumnTotals,
  QuoteChargeDealerInsights,
  QuoteChargeLevelTotals,
  QuoteChargeListingEntity,
  QuoteChargeProfitability,
  QuoteChargeRollup,
  QuoteChargeRowBreakdown,
  QuoteChargeTotals,
  QuoteLineItemType,
  QuotePriceBreakdown,
  QuotePriceComponentBreakdown,
} from '../types';

/** Prefix parent/self keys so equipment UUIDs cannot collide with charge UUIDs. */
const equipmentKey = (id: string) => `equipment:${id}`;
const chargeKey = (id: string) => `charge:${id}`;

const FEE_TYPES: readonly QuoteFeeType[] = [
  QuoteFeeType.DocFee,
  QuoteFeeType.TireFee,
  QuoteFeeType.TitleFee,
  QuoteFeeType.Other,
];

const TAX_TYPES: readonly QuoteFeeType[] = [
  QuoteFeeType.CityTax,
  QuoteFeeType.CountyTax,
  QuoteFeeType.StateTax,
  QuoteFeeType.FederalExciseTax,
  QuoteFeeType.VehicleInventoryTax,
];

interface CoreBase {
  /** Parent's own core Total Cost, before any children are added. */
  total: number;
  /** Parent's own core wholesale price (kept for future rule swaps). */
  wholesalePrice: number;
}

type CoreBaseByKey = Map<string, CoreBase>;

/**
 * Charge/equipment price engine for the invoice/cost-worksheet listing.
 *
 * `calc()` runs three passes and returns a fresh `QuoteChargeCalcResult`. The
 * input `QuoteChargeListingEntity[]` (form data) is NEVER mutated — the form
 * stays the source of truth for what the user typed, and the calc result is a
 * separate, verbose, read-only projection the UI consumes.
 *
 * Passes:
 *   1. Compute each row's OWN "core" price + a per-component breakdown.
 *   2. Recompute charge children whose markup/discount/tax are percentages —
 *      those must be a percentage of the PARENT's core, not of the child's own
 *      base (DR-10168, tech scope 3.2).
 *   3. Roll totals up the parent-child tree so a parent row's `rollup` holds
 *      `parent core + Σ children` — hidden AND visible children alike
 *      (DR-10168 invariant I1 / AC 1). Visible children still render their own
 *      detail row; the subtotal counts root lines only (Rule 3), so nothing is
 *      double-counted.
 *
 * The result exposes:
 *   - `totals` — canonical numbers (column totals, grand total, per-level
 *     split). These are view-independent: an invoice is the same invoice
 *     whether the dealer or the customer is looking at it. What differs
 *     between views is *presentation* (which rows show up, whether
 *     cost/markup columns are visible) — that stays on the UI side.
 *   - `dealer` — cost + margin, dealer-only insights that must never leak to
 *     the customer view (DR-10168 AC 12).
 *
 * Adding a new line-item type = extend `computeSelfPrice` + `getSelfKey` +
 * `getParentKey` + `getPrimaryPrice`. Everything downstream stays the same.
 */
class QuoteChargeCalculator {
  constructor(private readonly money: MoneyService = moneyService) {}

  public calc(input: QuoteChargeListingEntity[]): QuoteChargeCalcResult {
    const withOwnCore = input.map(entity => this.computeSelfPrice(entity));
    const coreBaseByKey = this.buildCoreBaseLookup(withOwnCore);
    const withParentAwareChildren = withOwnCore.map(row => this.recomputeChildAgainstParentBase(row, coreBaseByKey));
    const rows = this.rollupParentTotals(withParentAwareChildren);

    const totals = this.buildTotals(rows);
    const dealer = this.buildDealerInsights(rows);

    return {
      rows,
      totals,
      dealer,
      profitability: this.buildProfitability(rows),
    };
  }

  // ---------------------------------------------------------------------------
  // Pass 1 — each row against its OWN base, capturing a full breakdown
  // ---------------------------------------------------------------------------

  private computeSelfPrice(entity: QuoteChargeListingEntity): QuoteChargeCalcRow {
    const breakdown: QuoteChargeRowBreakdown = {};

    let nextCharge = entity.charge;
    if (entity.charge?.price) {
      const { price, breakdown: chargeBreakdown } = this.calculatePriceAgainstBase(entity.charge.price, null);
      nextCharge = { ...entity.charge, price };
      breakdown.charge = chargeBreakdown;
    }

    let nextEquipment = entity.equipment;
    if (entity.equipment?.price) {
      const { price, breakdown: equipmentBreakdown } = this.calculatePriceAgainstBase(entity.equipment.price, null);
      const nextAttachments =
        entity.equipment.attachments?.map(attachment => {
          const { price: attPrice, breakdown: attBreakdown } = this.calculatePriceAgainstBase(attachment.price, null);
          return { attachment: { ...attachment, price: attPrice }, breakdown: attBreakdown };
        }) ?? [];

      nextEquipment = {
        ...entity.equipment,
        price,
        attachments: nextAttachments.map(a => a.attachment),
      };
      breakdown.equipment = equipmentBreakdown;
      breakdown.attachments = nextAttachments.map(a => a.breakdown);
    }

    return {
      type: entity.type,
      charge: nextCharge,
      equipment: nextEquipment,
      breakdown,
    };
  }

  // ---------------------------------------------------------------------------
  // Pass 2 — children with %age fields use PARENT's core as base
  // ---------------------------------------------------------------------------

  private buildCoreBaseLookup(rows: QuoteChargeCalcRow[]): CoreBaseByKey {
    const lookup: CoreBaseByKey = new Map();

    rows.forEach(row => {
      const key = this.getSelfKey(row);
      const price = this.getPrimaryPrice(row);

      if (!key || !price) return;

      lookup.set(key, { total: price.total, wholesalePrice: price.wholesalePrice });
    });

    return lookup;
  }

  private recomputeChildAgainstParentBase(row: QuoteChargeCalcRow, coreBaseByKey: CoreBaseByKey): QuoteChargeCalcRow {
    // Only charge rows can be children today. Equipment rows are always roots
    // in their bucket; attachments are computed inline with their equipment.
    if (!row.charge?.price) return row;

    const parentKey = this.getParentKey(row);
    if (!parentKey) return row;

    const parentBase = coreBaseByKey.get(parentKey);
    if (!parentBase) return row;

    // Skip the extra work when nothing about this row is percent-based.
    if (!this.hasAnyPercentComponent(row.charge.price)) return row;

    const { price, breakdown } = this.calculatePriceAgainstBase(row.charge.price, parentBase);

    return {
      ...row,
      charge: { ...row.charge, price },
      breakdown: { ...row.breakdown, charge: breakdown },
    };
  }

  private hasAnyPercentComponent(price: BasePrice): boolean {
    return (
      price.markupType === DiscountType.Percent ||
      price.discountType === DiscountType.Percent ||
      price.salesTaxType === DiscountType.Percent
    );
  }

  // ---------------------------------------------------------------------------
  // Pass 3 — roll parent totals up the tree
  // ---------------------------------------------------------------------------

  private rollupParentTotals(rows: QuoteChargeCalcRow[]): QuoteChargeCalcRow[] {
    // A parent's displayed total is its own core value + the rolled total of
    // ALL its children, hidden AND visible (DR-10168 invariant I1 / AC 1).
    // Visible children still render their own detail row, but the subtotal only
    // counts root lines (Rule 3), so including them here does NOT double-count:
    // `buildTotals` sums atomic prices once, independently of these rollups.
    const rollupByParentKey = new Map<string, QuoteChargeRollup>();

    rows.forEach(row => {
      const parentKey = this.getParentKey(row);
      if (!parentKey) return;

      const childPrice = this.getPrimaryPrice(row);
      if (!childPrice) return;

      const acc = rollupByParentKey.get(parentKey) ?? this.emptyRollup();
      acc.rolledTotal += childPrice.total;
      acc.rolledNetPrice += childPrice.netPrice;
      acc.rolledSalesTax += childPrice.salesTax;
      rollupByParentKey.set(parentKey, acc);
    });

    if (rollupByParentKey.size === 0) return rows;

    return rows.map(row => {
      const selfKey = this.getSelfKey(row);
      const childrenContribution = selfKey ? rollupByParentKey.get(selfKey) : undefined;

      if (!childrenContribution) return row;

      const ownPrice = this.getPrimaryPrice(row);
      if (!ownPrice) return row;

      return {
        ...row,
        rollup: {
          // Children already post whole-cent values, so these sums are exact.
          rolledTotal: this.money.roundToCents(ownPrice.total + childrenContribution.rolledTotal),
          rolledNetPrice: this.money.roundToCents(ownPrice.netPrice + childrenContribution.rolledNetPrice),
          rolledSalesTax: this.money.roundToCents(ownPrice.salesTax + childrenContribution.rolledSalesTax),
        },
      };
    });
  }

  private emptyRollup(): QuoteChargeRollup {
    return { rolledTotal: 0, rolledNetPrice: 0, rolledSalesTax: 0 };
  }

  // ---------------------------------------------------------------------------
  // Price math (shared by all passes) — returns price + a full breakdown
  // ---------------------------------------------------------------------------

  /**
   * When `parentBase` is provided, percentage-typed markup/discount/tax use the
   * parent's core Total Cost as their base (per DR-10168 AC 3.2). Otherwise
   * percentages use the row's own wholesale/net price.
   */
  private calculatePriceAgainstBase(
    input: BasePrice,
    parentBase: CoreBase | null
  ): { price: BasePrice; breakdown: QuotePriceBreakdown } {
    const {
      wholesalePrice,
      retailPrice: originalRetailPrice,
      markup,
      markupType,
      discount,
      discountType,
      salesTax,
      salesTaxType,
    } = input;

    const ownWholesale = wholesalePrice || 0;
    const usingParent = parentBase !== null;

    const markupBase = usingParent ? parentBase!.total : ownWholesale;
    const markupAmount = this.resolveComponentAmount(markup, markupType, markupBase);
    const retailPrice = this.money.roundToCents(ownWholesale + markupAmount) || originalRetailPrice;

    const discountBase = usingParent ? parentBase!.total : retailPrice;
    const discountAmount = this.resolveComponentAmount(discount, discountType, discountBase);
    const netPrice = this.money.roundToCents(retailPrice - discountAmount);

    const taxBase = usingParent ? parentBase!.total : netPrice;
    const taxAmount = this.resolveComponentAmount(salesTax, salesTaxType, taxBase);

    const total = this.money.roundToCents(netPrice + taxAmount);

    const price: BasePrice = {
      total,
      markup,
      discount,
      netPrice,
      markupType,
      retailPrice,
      discountType,
      salesTaxType,
      salesTax: taxAmount,
      wholesalePrice: ownWholesale,
    };

    const breakdown: QuotePriceBreakdown = {
      input,
      ownWholesale,
      markup: this.componentBreakdown(markup, markupType, markupBase, markupAmount, usingParent),
      retailPrice,
      discount: this.componentBreakdown(discount, discountType, discountBase, discountAmount, usingParent),
      netPrice,
      salesTax: this.componentBreakdown(salesTax, salesTaxType, taxBase, taxAmount, usingParent),
      total,
    };

    return { price, breakdown };
  }

  private resolveComponentAmount(rateOrAmount: number, type: DiscountType, base: number): number {
    const amount = type === DiscountType.Percent ? base * (rateOrAmount / 100) : rateOrAmount;
    return this.money.roundToCents(amount);
  }

  private componentBreakdown(
    rateOrAmount: number,
    type: DiscountType,
    base: number,
    amount: number,
    usedParentBase: boolean
  ): QuotePriceComponentBreakdown {
    return { rateOrAmount, type, base, amount, usedParentBase };
  }

  // ---------------------------------------------------------------------------
  // Row helpers — kept small so new entity types are easy to add
  // ---------------------------------------------------------------------------

  private getPrimaryPrice(row: QuoteChargeCalcRow | QuoteChargeListingEntity): BasePrice | null {
    if (row.type === QuoteLineItemType.Equipment) return row.equipment?.price ?? null;
    return row.charge?.price ?? null;
  }

  private getSelfKey(row: QuoteChargeCalcRow | QuoteChargeListingEntity): string | null {
    if (row.type === QuoteLineItemType.Equipment) {
      const id = row.equipment?.equipmentId;
      return id ? equipmentKey(id) : null;
    }

    const id = row.charge?.id;
    return id ? chargeKey(id) : null;
  }

  private getParentKey(row: QuoteChargeCalcRow | QuoteChargeListingEntity): string | null {
    if (row.type !== QuoteLineItemType.Invoice) return null;

    const charge = row.charge;
    if (!charge) return null;

    // Explicit parent wins over the implicit bucket-based one.
    if (charge.parentId) {
      return charge.parentType === 'equipment' ? equipmentKey(charge.parentId) : chargeKey(charge.parentId);
    }

    // Implicit parent: a charge sitting inside an equipment bucket rolls up
    // into that equipment even when the user did not pick an explicit parent.
    if (charge.groupBucket?.type === ChargeAttachType.Equipment && charge.groupBucket.id) {
      return equipmentKey(charge.groupBucket.id);
    }

    return null;
  }

  /** Which "level" bucket a charge row contributes to. */
  private getChargeLevel(row: QuoteChargeCalcRow): 'invoice' | 'equipment' {
    const charge = row.charge;
    if (!charge) return 'invoice';

    if (charge.parentType === 'equipment') return 'equipment';
    if (charge.groupBucket?.type === ChargeAttachType.Equipment) return 'equipment';

    return 'invoice';
  }

  private isFeeCharge(row: QuoteChargeCalcRow): boolean {
    return !!row.charge && FEE_TYPES.includes(row.charge.type);
  }

  private isTaxCharge(row: QuoteChargeCalcRow): boolean {
    return !!row.charge && TAX_TYPES.includes(row.charge.type);
  }

  // ---------------------------------------------------------------------------
  // Aggregates
  // ---------------------------------------------------------------------------

  /**
   * View-independent totals. Walks every atomic price on the invoice (each
   * charge + each equipment + each attachment) exactly once, so the numbers
   * match whether the dealer or the customer is looking. `InternalOnly`
   * children still count here — they are part of the invoice; they are only
   * hidden as SEPARATE rows in the customer UI (their amount is already inside
   * the parent's `rollup`).
   */
  private buildTotals(rows: QuoteChargeCalcRow[]): QuoteChargeTotals {
    const columnTotals = this.columnTotals(rows);
    const byLevel = {
      invoice: this.emptyLevelTotals(),
      equipment: this.emptyLevelTotals(),
      attachments: this.emptyLevelTotals(),
    };

    rows.forEach(row => {
      if (row.charge?.price) {
        const level = this.getChargeLevel(row);
        byLevel[level].price += row.charge.price.total;
        if (this.isFeeCharge(row)) byLevel[level].fees += row.charge.price.total;
        if (this.isTaxCharge(row)) byLevel[level].taxes += row.charge.price.total;
      }

      if (row.equipment?.price) {
        byLevel.equipment.price += row.equipment.price.total;

        row.equipment.attachments?.forEach(attachment => {
          if (!attachment.price) return;
          byLevel.attachments.price += attachment.price.total;
        });
      }
    });

    return {
      columnTotals,
      grandTotal: columnTotals.total,
      byLevel,
    };
  }

  /**
   * Sum a subset of calc rows into column totals — same math as the table
   * footer, but scoped to whatever rows you pass in (e.g. a single group's
   * rows for an intermediate per-group subtotal). Each atomic price (charge +
   * equipment + attachments) is counted exactly once, so a group subtotal
   * never double-counts a rolled-up child.
   */
  public columnTotals(rows: QuoteChargeCalcRow[]): QuoteChargeColumnTotals {
    const totals = this.emptyColumnTotals();

    rows.forEach(row => {
      if (row.charge?.price) {
        this.addPriceToColumnTotals(totals, row.charge.price, row.breakdown.charge);
      }

      if (row.equipment?.price) {
        this.addPriceToColumnTotals(totals, row.equipment.price, row.breakdown.equipment);

        row.equipment.attachments?.forEach((attachment, index) => {
          if (!attachment.price) return;
          this.addPriceToColumnTotals(totals, attachment.price, row.breakdown.attachments?.[index]);
        });
      }
    });

    return totals;
  }

  /** Stable key for a row/entity, so callers can match form entities to calc rows. */
  public rowKey(row: QuoteChargeCalcRow | QuoteChargeListingEntity): string | null {
    return this.getSelfKey(row);
  }

  /** Dealer-only metrics. Never sent to the customer view. */
  private buildDealerInsights(rows: QuoteChargeCalcRow[]): QuoteChargeDealerInsights {
    let cost = 0;
    let margin = 0;

    rows.forEach(row => {
      if (row.charge?.price) {
        cost += row.charge.price.wholesalePrice;
        margin += row.breakdown.charge?.markup.amount ?? 0;
      }

      if (row.equipment?.price) {
        cost += row.equipment.price.wholesalePrice;
        margin += row.breakdown.equipment?.markup.amount ?? 0;

        row.equipment.attachments?.forEach((attachment, index) => {
          if (!attachment.price) return;
          cost += attachment.price.wholesalePrice;
          margin += row.breakdown.attachments?.[index]?.markup.amount ?? 0;
        });
      }
    });

    return { cost: this.money.roundToCents(cost), margin: this.money.roundToCents(margin) };
  }

  /**
   * Dealer profitability summary. Gross profit is a MARGIN-bearing figure, so it
   * must exclude pass-through lines (statutory fees) and sales tax that live at
   * the invoice level. In today's structure the margin-bearing lines are exactly
   * the equipment section — the unit plus its dealer-internal cost adjustments
   * (rebates, assists, vendor fees) rolled into it. Invoice-level fees and tax
   * are pass-through and never touch margin.
   *
   * So we scope the profitability math to equipment-level rows and read them
   * straight from the same column totals the equipment group subtotal shows:
   *   - Net Selling Price = equipment-section rolled NET price (excludes tax)
   *   - Total Dealer Cost = equipment-section rolled wholesale
   *   - Gross Profit      = the difference
   *
   * This reproduces Tom's internal cost worksheet to the cent (76,096.39 /
   * 81,097.00 / 5,000.61). It is a section-based proxy for the spec's per-line
   * `in_margin` flag; swap in that flag here once the data model carries it.
   */
  private buildProfitability(rows: QuoteChargeCalcRow[]): QuoteChargeProfitability {
    const marginRows = rows.filter(
      row => row.type === QuoteLineItemType.Equipment || this.getChargeLevel(row) === 'equipment'
    );
    const marginTotals = this.columnTotals(marginRows);

    const netSellingPrice = this.money.roundToCents(marginTotals.netPrice);
    const totalDealerCost = this.money.roundToCents(marginTotals.wholesalePrice);
    const grossProfit = this.money.roundToCents(netSellingPrice - totalDealerCost);
    const marginRate = netSellingPrice ? grossProfit / netSellingPrice : 0;

    return { netSellingPrice, totalDealerCost, grossProfit, marginRate };
  }

  /**
   * Adds one atomic price to the running column totals. When a `breakdown` is
   * supplied, the markup/discount columns use the amounts the calculator
   * ACTUALLY resolved (which, for percentage children, were computed against the
   * PARENT's core base — DR-10168 AC 3.2). Recomputing the percentage against
   * the row's own wholesale/retail here would disagree with the net/total that
   * were already posted, so the columns would no longer reconcile.
   */
  private addPriceToColumnTotals(
    totals: QuoteChargeColumnTotals,
    price: BasePrice,
    breakdown?: QuotePriceBreakdown
  ): void {
    totals.wholesalePrice += price.wholesalePrice;

    const markupAmount =
      breakdown?.markup.amount ??
      (price.markupType === DiscountType.Percent
        ? this.money.roundToCents(price.wholesalePrice * (price.markup / 100))
        : price.markup);
    totals.markup += markupAmount;

    totals.retailPrice += price.retailPrice;

    const discountAmount =
      breakdown?.discount.amount ??
      (price.discountType === DiscountType.Percent
        ? this.money.roundToCents(price.retailPrice * (price.discount / 100))
        : price.discount);
    totals.discount += discountAmount;

    totals.netPrice += price.netPrice;
    totals.salesTax += price.salesTax;
    totals.total += price.total;
  }

  private emptyColumnTotals(): QuoteChargeColumnTotals {
    return {
      wholesalePrice: 0,
      markup: 0,
      retailPrice: 0,
      discount: 0,
      netPrice: 0,
      salesTax: 0,
      total: 0,
    };
  }

  private emptyLevelTotals(): QuoteChargeLevelTotals {
    return { fees: 0, taxes: 0, price: 0 };
  }
}

export const quoteChargeCalculator = new QuoteChargeCalculator();

/** Empty result — safe default for initial state and empty listings. */
export const emptyChargeCalcResult: QuoteChargeCalcResult = {
  rows: [],
  totals: {
    columnTotals: {
      wholesalePrice: 0,
      markup: 0,
      retailPrice: 0,
      discount: 0,
      netPrice: 0,
      salesTax: 0,
      total: 0,
    },
    grandTotal: 0,
    byLevel: {
      invoice: { fees: 0, taxes: 0, price: 0 },
      equipment: { fees: 0, taxes: 0, price: 0 },
      attachments: { fees: 0, taxes: 0, price: 0 },
    },
  },
  dealer: { cost: 0, margin: 0 },
  profitability: { netSellingPrice: 0, totalDealerCost: 0, grossProfit: 0, marginRate: 0 },
};
